import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import GroupMembersHeader from '@web/src/features/group/group-members-header/group-members-header'
import { getMembershipBadge, getMembershipStatusBadge } from '@web/src/features/group/utils/group-badges'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { GroupMemberInfo, ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  BanIcon,
  EllipsisIcon,
  ShieldIcon,
  ShieldUserIcon,
  UserMinusIcon,
  UserSearchIcon,
  UsersIcon,
  Volume2Icon,
  VolumeXIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  GroupMemberSortEnum,
  GroupMemberStatus,
  GroupMembersPageGroupQuery,
  GroupMembersPageGroupQueryVariables,
  GroupMembersPageQuery,
  GroupMembersPageQueryVariables,
  GroupRole,
  RemoveGroupMemberMutation,
  RemoveGroupMemberMutationVariables,
  UpdateGroupMemberMutation,
  UpdateGroupMemberMutationVariables,
} from 'types/graphql'
import { z } from 'zod'

dayjs.extend(relativeTime)

export const GROUP_QUERY: TypedDocumentNode<GroupMembersPageGroupQuery, GroupMembersPageGroupQueryVariables> = gql`
  query GroupMembersPageGroupQuery($groupIdOrSlug: String!) {
    group: group(groupIdOrSlug: $groupIdOrSlug) {
      id
      slug
      allowMemberEvents
      ownerId
      name
      joinApproval
      canEdit
      canModerate
      canInvite
      memberCounts {
        totalCount
        activeCount
        mutedCount
        bannedCount
        ownerCount
        adminCount
        moderatorCount
        memberCount
      }
      joinRequestCounts {
        totalCount
        pendingCount
        approvedCount
        rejectedCount
      }
    }
  }
`

export const QUERY: TypedDocumentNode<GroupMembersPageQuery, GroupMembersPageQueryVariables> = gql`
  query GroupMembersPageQuery($page: Int, $filter: GroupMembersFilter, $sortBy: GroupMemberSortEnum) {
    groupMembersPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        ...GroupMemberInfo
      }
      count
      perPage
    }
  }
  ${GroupMemberInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const UPDATE_GROUP_MEMBER: TypedDocumentNode<UpdateGroupMemberMutation, UpdateGroupMemberMutationVariables> = gql`
  mutation UpdateGroupMemberMutation($id: String!, $input: UpdateGroupMemberInput!) {
    updateGroupMember(id: $id, input: $input) {
      ...GroupMemberInfo
    }
  }
  ${GroupMemberInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const REMOVE_GROUP_MEMBER: TypedDocumentNode<RemoveGroupMemberMutation, RemoveGroupMemberMutationVariables> = gql`
  mutation RemoveGroupMemberMutation($groupId: String!, $profileId: String!) {
    removeGroupMember(groupId: $groupId, profileId: $profileId) {
      id
    }
  }
`

type GroupMembersSearchInput = {
  page?: number
  name?: string
  role?: string
  status?: string
  sortBy?: string
}

type GroupMembersLoaderDeps = {
  page: number
  name?: string
  role?: string
  status?: string
  sortBy: string
}

function resolveGroupMembersSearchParams(search: GroupMembersSearchInput): GroupMembersLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.name,
    role: search.role,
    status: search.status,
    sortBy: search.sortBy ?? 'NEWEST',
  }
}

function groupMembersQueryVariables(
  groupIdOrSlug: string,
  deps: GroupMembersLoaderDeps
): GroupMembersPageQueryVariables {
  return {
    page: deps.page,
    filter: {
      groupId: groupIdOrSlug,
      name: deps.name,
      role: deps.role as GroupRole | undefined,
      status: deps.status as GroupMemberStatus | undefined,
    },
    sortBy: deps.sortBy as GroupMemberSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/group/$groupIdOrSlug_/members')({
  component: GroupMembersPageComponent,
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    role: z.string().optional(),
    status: z.string().optional(),
    sortBy: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        role: '',
        status: '',
        sortBy: 'NEWEST',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveGroupMembersSearchParams(search),
  loader: async ({ context, params, deps, location }) => {
    const resolvedDeps = deps as GroupMembersLoaderDeps
    const variables = groupMembersQueryVariables(params.groupIdOrSlug, resolvedDeps)

    const [{ queryRef }, groupPreload] = await Promise.all([
      queryAndPreload(context, QUERY, { variables }),
      queryAndPreload(context, GROUP_QUERY, {
        variables: { groupIdOrSlug: params.groupIdOrSlug },
      }),
    ])
    return {
      queryRef,
      pathname: location.pathname,
      groupQueryRef: groupPreload.queryRef,
      groupQueryData: groupPreload.queryData,
    }
  },
  head: () =>
    seo({
      title: `Members List | ${env.VITE_APP_NAME}`,
      description: 'Browse a list of group members.',
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    }),
})

function GroupMembersPageComponent() {
  const { t } = useTranslation()
  const { hasPermission, currentUser } = useAuth()
  const navigate = useNavigate()
  const formRef = useRef(null)
  const { groupIdOrSlug } = Route.useParams()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveGroupMembersSearchParams(search), [search])
  const loaderData = Route.useLoaderData()
  const { queryRef, groupQueryRef } = loaderData
  const [removeModal, setRemoveModal] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState(null)
  const { cache } = useApolloClient()

  const { data, error } = useReadQuery(queryRef as QueryRef<GroupMembersPageQuery>)
  const [isLoading, setIsLoading] = useState(false)

  const routerIsPending = useRouterState({ select: (s) => s.status === 'pending' })

  // Prevent hydration mismatch by only setting loading state on client
  useEffect(() => {
    setIsLoading(routerIsPending)
  }, [routerIsPending])

  const formDefaultValues = useMemo(
    () => ({
      page: resolvedSearch.page,
      name: resolvedSearch.name ?? '',
      role: resolvedSearch.role as GroupRole | undefined,
      status: resolvedSearch.status as GroupMemberStatus | undefined,
      sortBy: resolvedSearch.sortBy,
    }),
    [resolvedSearch.page, resolvedSearch.name, resolvedSearch.role, resolvedSearch.status, resolvedSearch.sortBy]
  )

  const formMethods = useForm({
    defaultValues: formDefaultValues,
  })
  const watchName = useWatch({ control: formMethods.control, name: 'name' })

  const { data: groupData, error: groupError } = useReadQuery(groupQueryRef as QueryRef<GroupMembersPageGroupQuery>)
  const resolvedGroupQuery = groupData ?? loaderData.groupQueryData

  const [updateGroupMember] = useMutation(UPDATE_GROUP_MEMBER, {
    onCompleted: () => {
      toast.success(t('Member updated successfully'))
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [removeGroupMember] = useMutation(REMOVE_GROUP_MEMBER, {
    onCompleted: (mutationData) => {
      toast.success(t('Member removed successfully'))
      // Remove the member from the cache
      cache.evict({ id: cache.identify(mutationData.removeGroupMember) })

      //refetchGroupData()
      setRemoveModal(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setRemoveModal(false)
    },
  })

  const filterResults = useCallback(() => {
    const curPage = formMethods.getValues('page')
    const name = formMethods.getValues('name')
    const role = formMethods.getValues('role') as GroupRole
    const status = formMethods.getValues('status') as GroupMemberStatus
    const sortBy = formMethods.getValues('sortBy') as GroupMemberSortEnum

    // Navigate with new search params - this will trigger the loader to re-run
    navigate({
      search: {
        page: curPage,
        name: name || undefined,
        sortBy: sortBy,
        role: role || undefined,
        status: status || undefined,
      },
      replace: true,
      viewTransition: false,
    } as never)
  }, [formMethods, navigate])

  const handleLiveFilter = useCallback(() => {
    filterResults()
  }, [filterResults])

  const handleResetFilters = () => {
    navigate({
      search: { page: 1 },
      replace: true,
      viewTransition: false,
    } as never)
  }

  const handlePageChange = useCallback(
    (nextPage: { page: number }) => {
      navigate({
        search: {
          ...search,
          page: nextPage.page,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      navigate({
        search: {
          ...search,
          page: 1,
          name: value || undefined,
        },
        replace: true,
        viewTransition: false,
      })
    },
    [navigate, search]
  )

  const handleMemberAction = (
    member: NonNullable<GroupMembersPageQuery['groupMembersPage']>['entries'][number],
    action: string
  ) => {
    if (action === 'remove') {
      setMemberToRemove(member)
      setRemoveModal(true)
    } else if (action === 'set-admin-role') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: { role: 'ADMIN' },
        },
      })
    } else if (action === 'set-moderator-role') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: { role: 'MODERATOR' },
        },
      })
    } else if (action === 'set-member-role') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: { role: 'MEMBER' },
        },
      })
    } else if (action === 'mute') {
      const mutedUntil = new Date()
      mutedUntil.setDate(mutedUntil.getDate() + 7) // Mute for 7 days
      updateGroupMember({
        variables: {
          id: member.id,
          input: {
            status: 'MUTED',
            mutedUntil: mutedUntil.toISOString(),
          },
        },
      })
    } else if (action === 'unmute') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: {
            status: 'ACTIVE',
            mutedUntil: null,
          },
        },
      })
    } else if (action === 'ban') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: { status: 'BANNED' },
        },
      })
    } else if (action === 'unban') {
      updateGroupMember({
        variables: {
          id: member.id,
          input: { status: 'ACTIVE' },
        },
      })
    }
  }

  const pageData = useMemo(
    () => data?.groupMembersPage || { entries: [], count: 0, perPage: 24 },
    [data?.groupMembersPage]
  )
  const currentPage = useWatch({ control: formMethods.control, name: 'page' })

  if (error || groupError) {
    return <ErrorAlert error={error || groupError} />
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-2">
      <div className="flex w-full flex-col items-stretch gap-4">
        <GroupMembersHeader activeTab={'members'} group={resolvedGroupQuery?.group} />

        <Form className="w-full" formMethods={formMethods} ref={formRef}>
          <div className="flex w-full flex-col items-stretch gap-4">
            {/* Search and Filters */}
            <SearchExpand
              clearSearchLabel={t('Clear search')}
              inputProps={{ 'aria-label': t('Search members') }}
              loading={isLoading}
              minChars={0}
              name="name"
              onSearchChange={handleSearchChange}
              placeholder={t('Search members by name...')}
              toggleFiltersLabel={t('Toggle filters')}
            >
              <Card className="gap-0 py-0">
                <CardContent className="px-2 py-3">
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-wrap items-start gap-2">
                      <NativeSelectFormField label={t('Roles')} name="role" onValueChange={handleLiveFilter}>
                        <option value="">
                          {t('All Roles ({{count}})', {
                            count: resolvedGroupQuery?.group?.memberCounts?.totalCount || 0,
                          })}
                        </option>
                        <option value="OWNER">
                          {t('Owners ({{count}})', { count: resolvedGroupQuery?.group?.memberCounts?.ownerCount || 0 })}
                        </option>
                        <option value="ADMIN">
                          {t('Admins ({{count}})', { count: resolvedGroupQuery?.group?.memberCounts?.adminCount || 0 })}
                        </option>
                        <option value="MODERATOR">
                          {t('Moderators ({{count}})', {
                            count: resolvedGroupQuery?.group?.memberCounts?.moderatorCount || 0,
                          })}
                        </option>
                        <option value="MEMBER">
                          {t('Members ({{count}})', {
                            count: resolvedGroupQuery?.group?.memberCounts?.memberCount || 0,
                          })}
                        </option>
                      </NativeSelectFormField>

                      {resolvedGroupQuery?.group?.canModerate ? (
                        <NativeSelectFormField label={t('Status')} name="status" onValueChange={handleLiveFilter}>
                          <option value="">
                            {t('All Status ({{count}})', {
                              count: resolvedGroupQuery?.group?.memberCounts?.totalCount || 0,
                            })}
                          </option>
                          <option value="ACTIVE">
                            {t('Active ({{count}})', {
                              count: resolvedGroupQuery?.group?.memberCounts?.activeCount || 0,
                            })}
                          </option>
                          <option value="MUTED">
                            {t('Muted ({{count}})', {
                              count: resolvedGroupQuery?.group?.memberCounts?.mutedCount || 0,
                            })}
                          </option>
                          <option value="BANNED">
                            {t('Banned ({{count}})', {
                              count: resolvedGroupQuery?.group?.memberCounts?.bannedCount || 0,
                            })}
                          </option>
                        </NativeSelectFormField>
                      ) : null}

                      <NativeSelectFormField label={t('Sort By')} name="sortBy" onValueChange={handleLiveFilter}>
                        <option value="NEWEST">{t('Newest')}</option>
                        <option value="OLDEST">{t('Oldest')}</option>
                        <option value="NAME">{t('Name')}</option>
                        {resolvedGroupQuery?.group?.canModerate ? <option value="ROLE">{t('Role')}</option> : null}
                        {resolvedGroupQuery?.group?.canModerate ? <option value="STATUS">{t('Status')}</option> : null}
                      </NativeSelectFormField>
                    </div>

                    <div className="flex w-full justify-end">
                      <Button onClick={handleResetFilters} type="button" variant="secondary">
                        {t('Reset')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SearchExpand>

            {/* Results */}
            {pageData.entries.length > 0 && (
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full flex-col gap-1">
                  {pageData.entries.map((member) => {
                    const canManageThisMember =
                      (resolvedGroupQuery?.group?.canEdit &&
                        member.profile.id !== currentUser.profile.id &&
                        member.role !== 'OWNER') ||
                      hasPermission('group:update:all')

                    const canModerateThisMember =
                      (resolvedGroupQuery?.group?.canModerate &&
                        member.profile.id !== currentUser.profile.id &&
                        !['OWNER', 'ADMIN'].includes(member.role)) ||
                      hasPermission('group:update:all')

                    return (
                      <Card
                        className="w-full cursor-pointer overflow-hidden border-border bg-muted/50 py-0 transition-colors hover:border-ring hover:bg-muted data-[size=sm]:py-1"
                        key={member.id}
                        size="sm"
                      >
                        <CardContent className="px-3 py-2">
                          <div className="flex w-full items-center justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center">
                              <SFProfileBanner
                                className="cursor-pointer"
                                lowerTitle={
                                  <div className="flex flex-col items-start gap-0">
                                    <div className="flex flex-wrap items-center gap-1">
                                      {member.role !== 'MEMBER' ? getMembershipBadge(member, 'sm') : null}
                                      {resolvedGroupQuery?.group?.canModerate
                                        ? getMembershipStatusBadge(member.status, member.mutedUntil, 'sm')
                                        : null}
                                    </div>

                                    <p className="line-clamp-1 text-muted-foreground text-xs">
                                      {t('Joined: {{time}}', { time: dayjs(member.joinedAt).fromNow() })}
                                    </p>

                                    {member.invitedBy && resolvedGroupQuery?.group?.canModerate ? (
                                      <p className="line-clamp-1 text-muted-foreground text-xs">
                                        {t('Invited by: {{name}}', { name: member.invitedBy.name })}
                                      </p>
                                    ) : null}
                                  </div>
                                }
                                onClick={() =>
                                  navigate({
                                    to: '/profile/$profileId/about',
                                    params: { profileId: member.profile.id },
                                  } as never)
                                }
                                profile={member.profile}
                                size="xl"
                              />
                            </div>

                            {canManageThisMember || canModerateThisMember ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger
                                  render={
                                    <Button
                                      aria-label={t('Member actions')}
                                      className="shrink-0 rounded-full"
                                      size="icon"
                                      type="button"
                                    >
                                      <EllipsisIcon />
                                    </Button>
                                  }
                                />
                                <DropdownMenuContent align="end">
                                  {canManageThisMember ? (
                                    <>
                                      {member.role !== 'ADMIN' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'set-admin-role')}>
                                          <ShieldUserIcon />
                                          {t('Set Role to Admin')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      {member.role !== 'MODERATOR' ? (
                                        <DropdownMenuItem
                                          onClick={() => handleMemberAction(member, 'set-moderator-role')}
                                        >
                                          <ShieldIcon />
                                          {t('Set Role to Moderator')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      {member.role !== 'MEMBER' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'set-member-role')}>
                                          <UsersIcon />
                                          {t('Set Role to Member')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      <DropdownMenuSeparator />
                                    </>
                                  ) : null}

                                  {canModerateThisMember || canManageThisMember ? (
                                    <>
                                      {member.status === 'ACTIVE' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'mute')}>
                                          <VolumeXIcon />
                                          {t('Mute (7 days)')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      {member.status === 'MUTED' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'unmute')}>
                                          <Volume2Icon />
                                          {t('Unmute')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      {member.status !== 'BANNED' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'ban')}>
                                          <BanIcon />
                                          {t('Ban')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      {member.status === 'BANNED' ? (
                                        <DropdownMenuItem onClick={() => handleMemberAction(member, 'unban')}>
                                          <UsersIcon />
                                          {t('Unban')}
                                        </DropdownMenuItem>
                                      ) : null}

                                      <DropdownMenuSeparator />
                                    </>
                                  ) : null}

                                  {canManageThisMember ? (
                                    <DropdownMenuItem
                                      onClick={() => handleMemberAction(member, 'remove')}
                                      variant="destructive"
                                    >
                                      <UserMinusIcon />
                                      {t('Remove from Group')}
                                    </DropdownMenuItem>
                                  ) : null}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : null}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>

                <PaginationControl
                  count={pageData.count}
                  onPageChange={handlePageChange}
                  page={currentPage}
                  pageSize={pageData.perPage}
                />
              </div>
            )}

            {pageData.entries.length === 0 && (
              <Empty className="py-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <UserSearchIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No members found')}</EmptyTitle>
                  {watchName && <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>}
                </EmptyHeader>
              </Empty>
            )}
          </div>
        </Form>
      </div>

      {/* Remove Member Modal */}
      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={() => {
          if (memberToRemove) {
            removeGroupMember({
              variables: {
                groupId: groupIdOrSlug,
                profileId: memberToRemove.profile.id,
              },
            })
          }
        }}
        onOpenChange={(e) => setRemoveModal(e.open)}
        open={removeModal}
        text={t('Are you sure you want to remove {{name}} from the group?', { name: memberToRemove?.profile?.name })}
        title={t('Remove Member')}
        variant="delete"
      />
    </div>
  )
}
